import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { HiOutlineLockClosed, HiOutlineTrendingUp } from 'react-icons/hi';
import { TbWallet } from 'react-icons/tb';
import { api } from '../services/api';
import { useWallet } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { TeamSkeleton } from '../components/Skeleton';
import PageHeader from '../components/PageHeader';

const MAX_LEVEL = 15;
const LEVEL_TONES = ['cyan', 'green', 'gold', 'blue', 'violet', 'rose'];

function usdt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function share(have, need) {
  const required = Number(need) || 0;
  if (required <= 0) return 1;
  return Math.max(0, Math.min(1, (Number(have) || 0) / required));
}

function Ring({ value, max }) {
  const size = 148;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - share(value, max));
  return (
    <div className="level-ring" aria-hidden>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle className="level-ring__track" cx={size / 2} cy={size / 2} r={radius} />
        <circle
          className="level-ring__value"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="level-ring__label">
        <strong>{value}</strong>
        <span>of {max}</span>
      </div>
    </div>
  );
}

function Meter({ label, hint, have, need, valueText }) {
  const pct = Math.round(share(have, need) * 100);
  return (
    <div className="level-meter">
      <div className="level-meter__top">
        <span>{label}</span>
        <strong>{valueText || `${have} / ${need}`}</strong>
      </div>
      <div className="level-meter__track">
        <div className="level-meter__fill" style={{ width: `${pct}%` }} />
      </div>
      {hint ? <p className="muted">{hint}</p> : null}
    </div>
  );
}

export default function LevelPage() {
  const { user, openConnect, connectOpening, loading: walletLoading } = useWallet();
  const toast = useToast();
  const [charts, setCharts] = useState(null);
  const [unlock, setUnlock] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (walletLoading) return undefined;
    if (!user) {
      setCharts(null);
      setUnlock(null);
      setPageLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPageLoading(true);
    api
      .myCommissions()
      .then((data) => {
        if (cancelled) return;
        setCharts(data.charts || null);
        setUnlock(data.unlock || null);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.message || 'Failed to load level');
      })
      .finally(() => {
        if (!cancelled) setPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.wallet_address, walletLoading, toast]);

  if (walletLoading || (user && pageLoading)) {
    return <TeamSkeleton />;
  }

  if (!user) {
    return (
      <>
        <PageHeader title="Level" subtitle="Unlock deeper downline commission" />
        <motion.div className="panel panel--glow" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <h2>Connect to see your level</h2>
          <button
            className={`btn btn--primary${connectOpening ? ' is-busy' : ''}`}
            type="button"
            onClick={() => openConnect('Connect wallet to view level')}
            disabled={connectOpening}
            aria-busy={connectOpening}
          >
            {connectOpening ? <span className="btn__spin" aria-hidden /> : <TbWallet />}
            {connectOpening ? 'Connecting…' : 'Connect Wallet'}
          </button>
        </motion.div>
      </>
    );
  }

  const roi = charts?.roi_downline;
  const levels = roi?.levels || [];
  const progress = new Map((unlock?.levels || []).map((row) => [row.level, row]));
  const current = Number(unlock?.level || 0);
  const nextRow = levels.find((row) => row.level === current + 1) || null;
  const nextState = nextRow ? progress.get(nextRow.level) : null;
  const invested = Number(user.total_invested || 0);
  const minSelf = Number(roi?.min_self_invest ?? 100);
  const eligible = invested + 1e-8 >= minSelf;
  const atMax = current >= MAX_LEVEL;

  return (
    <>
      <PageHeader title="Level" subtitle="Your unlock and what the next level needs" />
      <div className="level-page">
        <motion.section className="panel level-hero" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Ring value={current} max={MAX_LEVEL} />
          <div>
            <p className="eyebrow"><HiOutlineTrendingUp /> Account level</p>
            <h2>Level {current} unlocked</h2>
            <p className="muted">
              {atMax
                ? 'Every commission level is open on your account.'
                : `Level ${current + 1} is next. It opens only after the counts below are met.`}
            </p>
            <p className={eligible ? 'level-pill level-pill--ok' : 'level-pill level-pill--wait'}>
              {eligible
                ? `Eligible · ${usdt(invested)} USDT invested`
                : `Invest ${usdt(Math.max(0, minSelf - invested))} more USDT to receive commission`}
            </p>
          </div>
        </motion.section>

        <motion.section className="panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h2>{atMax ? 'All levels open' : `Progress to level ${current + 1}`}</h2>
          {atMax ? (
            <p className="muted">You can receive downline commission through level 15.</p>
          ) : (
            <>
              {nextRow && Number(nextRow.gen1_count) > 0 ? (
                <Meter
                  label="1st level directs"
                  hint={`Each needs at least ${usdt(nextRow.gen1_min)} USDT invested`}
                  have={nextState?.gen1 ?? 0}
                  need={nextRow.gen1_count}
                />
              ) : null}
              {nextRow && Number(nextRow.gen2_count) > 0 ? (
                <Meter
                  label="2nd level users"
                  hint={`Each needs at least ${usdt(nextRow.gen2_min)} USDT invested`}
                  have={nextState?.gen2 ?? 0}
                  need={nextRow.gen2_count}
                />
              ) : null}
              {minSelf > 0 ? (
                <Meter
                  label="Your investment"
                  hint={eligible ? 'You can receive unlocked commission.' : `Minimum is ${usdt(minSelf)} USDT.`}
                  have={invested}
                  need={minSelf}
                  valueText={`${usdt(invested)} / ${usdt(minSelf)} USDT`}
                />
              ) : null}
            </>
          )}
        </motion.section>

        <motion.section className="panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h2>All levels</h2>
          <div className="level-ladder">
            {levels.map((row) => {
              const state = progress.get(row.level);
              const open = Boolean(state?.met);
              const isNext = row.level === current + 1;
              const bits = [];
              if (Number(row.gen1_count) > 0) bits.push(`${row.gen1_count} directs · ${usdt(row.gen1_min)}`);
              if (Number(row.gen2_count) > 0) bits.push(`${row.gen2_count} on 2nd · ${usdt(row.gen2_min)}`);
              const tone = LEVEL_TONES[(row.level - 1) % LEVEL_TONES.length];
              return (
                <article
                  key={row.level}
                  className={`level-step${open ? ` is-open level-step--${tone}` : ''}${isNext ? ' is-next' : ''}`}
                >
                  <div className="level-step__top">
                    <strong>{row.level}</strong>
                    <span className="level-step__status">
                      {open ? 'Open' : isNext ? 'Next' : <><HiOutlineLockClosed /> Locked</>}
                    </span>
                  </div>
                  {bits.length ? <p>{bits.join(' · ')}</p> : null}
                  {!open && state && (Number(row.gen1_count) > 0 || Number(row.gen2_count) > 0) ? (
                    <p className="muted">
                      {Number(row.gen1_count) > 0 ? `${state.gen1}/${row.gen1_count}` : ''}
                      {Number(row.gen1_count) > 0 && Number(row.gen2_count) > 0 ? ' · ' : ''}
                      {Number(row.gen2_count) > 0 ? `${state.gen2}/${row.gen2_count}` : ''}
                    </p>
                  ) : null}
                </article>
              );
            })}
          </div>
        </motion.section>
      </div>
    </>
  );
}
